import { useState } from "react";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import { ActivityItem } from "../components/Dashboard/DashboardActivity";
import { ListCard } from "../components/Dashboard/DashboardList";
import { StatCard } from "../components/Dashboard/DashboardStat";
import {
  useGetDashboardStats,
  useGetTopClients,
  useGetRecentActivity,
  useGetExpiringSubscriptions,
} from "../api/dashboard.queries";
import { formatActivityTimestamp } from "@/lib/dateUtils";

export default function DashboardContainer() {
  const [toggleCard, setToggleCard] = useState(false);

  // Fetch dashboard data
  const { data: stats, isLoading: statsLoading } = useGetDashboardStats();
  const { data: topClientsData, isLoading: topClientsLoading } =
    useGetTopClients({ limit: 6 });
  const { data: recentActivityData, isLoading: activityLoading } =
    useGetRecentActivity({ limit: 4 });
  const { data: expiringData, isLoading: expiringLoading } =
    useGetExpiringSubscriptions({ days_ahead: 12 });

  // Loading state
  const isLoading =
    statsLoading || topClientsLoading || activityLoading || expiringLoading;

  // Transform data for UI
  const topClientsForUI =
    topClientsData?.top_clients.map((client) => ({
      title: client.name,
      plan: client.plan,
      main: client.total_usage.toLocaleString(),
      sub: `${client.usage_percentage}%`,
    })) || [];

  const expiringForUI =
    expiringData?.expiring_subscriptions.map((sub) => ({
      title: sub.client_name,
      plan: sub.plan,
      main: new Date(sub.expires_at).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
      sub: `(in ${sub.days_until_expiry} days)`,
    })) || [];

  // Loading state
  if (isLoading) {
    return (
      <main className="flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
        <AdminBreadcrumb title="Dashboard" />
        <div className="min-h-0 flex-1">
          <div className="scrollbar-thin grid min-h-0 auto-rows-max gap-4 pb-20">
            {/* Skeleton loaders */}
            <div className="grid h-auto gap-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-32 animate-pulse rounded-lg bg-white/10"
                />
              ))}
            </div>
            <div className="grid h-auto gap-3 md:grid-cols-1 lg:grid-cols-2">
              {[...Array(2)].map((_, i) => (
                <div
                  key={i}
                  className="h-96 animate-pulse rounded-lg bg-white/10"
                />
              ))}
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
      <AdminBreadcrumb title="Dashboard" />
      <div className="min-h-0 flex-1">
        <div className="scrollbar-thin grid min-h-0 auto-rows-max gap-4 pb-20">
          {/* Stats Cards */}
          <div className="grid h-auto gap-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            <StatCard
              label="Active Clients"
              value={stats?.active_clients.count ?? 0}
              helper={stats?.active_clients.label ?? ""}
              className="cursor-default"
            >
              <i className="ri-group-line text-black-400 text-[14px]" />
            </StatCard>
            <StatCard
              label="Expiring Soon"
              value={stats?.expiring_soon.count ?? 0}
              helper={stats?.expiring_soon.label ?? ""}
              className="cursor-default"
            >
              <i className="ri-pass-expired-line text-black-400 text-[14px]" />
            </StatCard>
            <StatCard
              label="System Health"
              value={`${stats?.system_health.percentage ?? 0}%`}
              helper={stats?.system_health.label ?? ""}
              className="col-span-1 cursor-default md:col-span-2 lg:col-span-1"
            >
              <i className="ri-pulse-line text-black-400 text-[14px]" />
            </StatCard>
          </div>

          {/* Lists */}
          <div className="grid h-auto gap-3 md:grid-cols-1 lg:grid-cols-2">
            {!toggleCard ? (
              <ListCard
                title="Top Clients"
                subtitle="Clients ranked by total API usage"
                items={topClientsForUI}
                toggleCard={toggleCard}
                setToggleCard={setToggleCard}
                showExpiringSubscriptions={true}
                className="cursor-default"
              />
            ) : (
              <ListCard
                title="Expiring Subscriptions"
                subtitle="Subscriptions expiring in the next 12 days"
                items={expiringForUI}
                toggleCard={toggleCard}
                setToggleCard={setToggleCard}
                showExpiringSubscriptions={true}
                className="cursor-default"
              />
            )}
            <ListCard
              title="Recent Activity"
              subtitle="Latest API calls and transactions"
              items={
                recentActivityData?.recent_activity.map((activity, i) => (
                  <ActivityItem
                    key={activity.activity_id || i}
                    action={activity.endpoint}
                    client={activity.client_name}
                    time={formatActivityTimestamp(activity.timestamp)}
                    status={activity.status}
                  />
                )) || []
              }
              className="cursor-default"
            />
          </div>
        </div>
      </div>
    </main>
  );
}
